import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { X, Plus, Minus, ShoppingCart, Trash2, ArrowRight, Zap, Truck } from 'lucide-react';
import { categories, SHIPPING_METHOD, FREE_SHIPPING_THRESHOLD } from '../../data/mock';

const CartDrawer = () => {
  const { 
    isCartOpen, 
    closeCart, 
    cartItems, 
    cartTotal, 
    updateQuantity, 
    removeFromCart 
  } = useCart();

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50"
        onClick={closeCart}
      />
      
      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-slate-900 z-50 shadow-2xl flex flex-col border-l border-slate-800">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-800 bg-gradient-to-r from-vybe-600 to-vybe-500">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-white/20 rounded-2xl flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-display text-lg text-white">Your Cart</h2>
              <p className="text-sm text-vybe-100">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</p>
            </div>
          </div>
          <button 
            onClick={closeCart}
            className="p-2.5 hover:bg-white/10 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-5 bg-slate-950">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-24 h-24 bg-slate-800 rounded-3xl flex items-center justify-center mb-5">
                <Zap className="w-10 h-10 text-slate-600" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">Your cart is empty</h3>
              <p className="text-slate-400 text-sm mb-6">Start shopping for keto supplements</p>
              <button 
                onClick={closeCart}
                className="px-6 py-3 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white font-semibold rounded-xl transition-all shadow-button"
              >
                Browse Products
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map(item => {
                const itemCategory = categories.find(c => c.id === item.categoryId);
                return (
                  <div key={item.id} className="flex gap-4 p-4 bg-slate-800/50 rounded-2xl border border-slate-700/50">
                    {/* Image */}
                    <div className="w-20 h-20 bg-slate-800 rounded-xl overflow-hidden flex-shrink-0">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-vybe-400 uppercase tracking-wider font-semibold mb-1">
                        {itemCategory?.name || 'Supplements'}
                      </p>
                      <h4 className="font-medium text-white text-sm mb-1.5 line-clamp-2">
                        {item.name}
                      </h4>
                      <p className="text-white font-bold">
                        ${item.price.toFixed(2)}
                      </p>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-slate-700 rounded-xl bg-slate-900 overflow-hidden">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-2 hover:bg-slate-800 transition-colors"
                          >
                            <Minus className="w-3 h-3 text-slate-400" />
                          </button>
                          <span className="w-8 text-center text-sm font-semibold text-white">
                            {item.quantity}
                          </span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-2 hover:bg-slate-800 transition-colors"
                          >
                            <Plus className="w-3 h-3 text-slate-400" />
                          </button>
                        </div>
                        
                        <button 
                          onClick={() => removeFromCart(item.id)}
                          className="p-2 text-coral-500 hover:bg-coral-500/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-slate-800 p-5 space-y-4 bg-slate-900">
            {/* Subtotal */}
            <div className="flex items-center justify-between">
              <span className="text-slate-400 font-medium">Subtotal</span>
              <div className="text-right">
                <span className="text-2xl font-bold text-white">${cartTotal.toFixed(2)}</span>
                <span className="text-sm text-slate-500 ml-1">USD</span>
              </div>
            </div>
            
            {/* Free Shipping Notice */}
            {cartTotal < FREE_SHIPPING_THRESHOLD && (
              <div className="bg-vybe-600/10 p-4 rounded-xl text-center border border-vybe-500/30">
                <p className="text-vybe-300 text-sm">
                  Add <span className="font-bold text-vybe-400">${(FREE_SHIPPING_THRESHOLD - cartTotal).toFixed(2)}</span> more for free shipping!
                </p>
              </div>
            )}
            
            {cartTotal >= FREE_SHIPPING_THRESHOLD && (
              <div className="bg-cyan-600/10 p-4 rounded-xl text-center border border-cyan-500/30">
                <p className="text-cyan-400 text-sm font-medium">
                  ✓ You qualify for free shipping!
                </p>
              </div>
            )}
            
            {/* Checkout Button */}
            <Link 
              to="/checkout" 
              onClick={closeCart}
              className="flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-vybe-600 to-vybe-500 hover:from-vybe-500 hover:to-vybe-400 text-white font-bold rounded-xl transition-all shadow-button"
            >
              Proceed to Checkout
              <ArrowRight className="w-5 h-5" />
            </Link>
            
            <div className="flex items-center justify-center gap-2 text-slate-500 text-xs">
              <Truck className="w-3 h-3" />
              <span>Ships via {SHIPPING_METHOD} | USA only</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
